// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import api from './advisorApi';
import type {
  Quote,
  QuoteCreateRequest,
  QuoteOption,
  QuoteOptionsResponse,
  QuoteType,
  QuoteUpdateRequest,
} from '../types/quote';
import type { PageResponse } from '../types/client';

export interface ListQuotesParams {
  page?: number;
  size?: number;
  quoteType?: QuoteType;
  search?: string;
}

/** A distinct Product Catalogue entry within a category (drives the type dropdowns). */
export interface ProductCatalogueEntry {
  code: string;
  name: string;
  category: string;
}

export const quoteApi = {
  /**
   * The Product Catalogue for a category — distinct active product codes + names
   * (provider-agnostic). The quote's type field stores the chosen {@code code},
   * which the terms engine uses to constrain the option fan-out to that product.
   */
  getProductCatalogue: async (category: string): Promise<ProductCatalogueEntry[]> => {
    const response = await api.get<ProductCatalogueEntry[]>('/policy-types/product-catalogue', {
      params: { category },
    });
    return response.data;
  },

  /**
   * Create a new quote request. Backend persists the quote in PENDING status
   * and dispatches asynchronous provider option generation. Frontend should
   * navigate to the quote options page and poll {@code getQuoteOptions} until
   * status === 'COMPLETED'.
   */
  createQuote: async (data: QuoteCreateRequest): Promise<Quote> => {
    const response = await api.post<Quote>('/quotes', data);
    return response.data;
  },

  listQuotes: async (
    profileId: string,
    params: ListQuotesParams = {},
  ): Promise<PageResponse<Quote>> => {
    const response = await api.get<PageResponse<Quote>>(
      `/quotes/profile/${profileId}`,
      {
        params: {
          page: params.page ?? 0,
          size: params.size ?? 20,
          ...(params.quoteType ? { quoteType: params.quoteType } : {}),
          ...(params.search ? { search: params.search } : {}),
        },
      },
    );
    return response.data;
  },

  getQuote: async (quoteId: string): Promise<Quote> => {
    const response = await api.get<Quote>(`/quotes/${quoteId}`);
    return response.data;
  },

  /**
   * Mark the quote as COMPLETED — advisor produced the Statement of
   * Suitability for the selected option. Backend rejects with 400 if no
   * option has been selected on the quote yet. Idempotent.
   */
  completeQuote: async (quoteId: string): Promise<Quote> => {
    const response = await api.post<Quote>(`/quotes/${quoteId}/complete`);
    return response.data;
  },

  updateQuote: async (
    quoteId: string,
    data: QuoteUpdateRequest,
  ): Promise<Quote> => {
    const response = await api.put<Quote>(`/quotes/${quoteId}`, data);
    return response.data;
  },

  /**
   * Polling endpoint. Returns the options received so far + an aggregate
   * status. Frontend stops polling when {@code status === 'COMPLETED'}.
   */
  getQuoteOptions: async (quoteId: string): Promise<QuoteOptionsResponse> => {
    const response = await api.get<QuoteOptionsResponse>(
      `/quotes/${quoteId}/options`,
    );
    return response.data;
  },

  selectOption: async (
    quoteId: string,
    optionId: string,
  ): Promise<QuoteOption> => {
    const response = await api.post<QuoteOption>(
      `/quotes/${quoteId}/options/${optionId}/select`,
    );
    return response.data;
  },

  deselectOption: async (
    quoteId: string,
    optionId: string,
  ): Promise<QuoteOption> => {
    const response = await api.delete<QuoteOption>(
      `/quotes/${quoteId}/options/${optionId}/select`,
    );
    return response.data;
  },

  pinOption: async (
    quoteId: string,
    optionId: string,
  ): Promise<QuoteOption> => {
    const response = await api.post<QuoteOption>(
      `/quotes/${quoteId}/options/${optionId}/pin`,
    );
    return response.data;
  },

  unpinOption: async (
    quoteId: string,
    optionId: string,
  ): Promise<QuoteOption> => {
    const response = await api.delete<QuoteOption>(
      `/quotes/${quoteId}/options/${optionId}/pin`,
    );
    return response.data;
  },

  deleteQuote: async (quoteId: string): Promise<void> => {
    await api.delete(`/quotes/${quoteId}`);
  },
};
